import { FormattedMessage, useIntl } from 'react-intl';

import type { HtmlSourceValidationMessage } from '../html-validation';

export interface HtmlSourceValidationPanelProps {
    messages: HtmlSourceValidationMessage[];
}

const getValidationMessageText = (intl: ReturnType<typeof useIntl>, message: HtmlSourceValidationMessage): string => {
    switch (message.code) {
        case 'unexpected-closing-tag':
            return intl.formatMessage(
                {
                    id: 'content-detail.editor.validation.unexpected-closing-tag',
                    description: 'Validation error shown when HTML template source has an unexpected closing tag',
                    defaultMessage: 'Unexpected closing tag: {tag}.'
                },
                message.values
            );
        case 'expected-closing-tag':
            return intl.formatMessage(
                {
                    id: 'content-detail.editor.validation.expected-closing-tag',
                    description: 'Validation error shown when HTML template source closes a different tag than expected',
                    defaultMessage: 'Expected closing tag {expectedTag} before {actualTag}.'
                },
                message.values
            );
        case 'missing-closing-tag':
            return intl.formatMessage(
                {
                    id: 'content-detail.editor.validation.missing-closing-tag',
                    description: 'Validation error shown when HTML template source is missing a closing tag',
                    defaultMessage: 'Missing closing tag: {tag}.'
                },
                message.values
            );
        case 'unbalanced-css-braces':
            return intl.formatMessage({
                id: 'content-detail.editor.validation.unbalanced-css-braces',
                description: 'Validation error shown when an HTML template CSS block has unbalanced braces',
                defaultMessage: 'CSS block has unbalanced braces.'
            });
        case 'missing-css-semicolon':
            return intl.formatMessage(
                {
                    id: 'content-detail.editor.validation.missing-css-semicolon',
                    description: 'Validation warning shown when an HTML template CSS declaration misses a semicolon',
                    defaultMessage: 'CSS declaration "{declaration}" does not end with a semicolon.'
                },
                message.values
            );
        case 'unsafe-tag':
            return intl.formatMessage(
                {
                    id: 'content-detail.editor.validation.unsafe-tag',
                    description: 'Validation error shown when HTML template source contains a disallowed HTML tag',
                    defaultMessage: 'The {tag} tag is not allowed in this HTML.'
                },
                message.values
            );
        case 'inline-event-handler':
            return intl.formatMessage({
                id: 'content-detail.editor.validation.inline-event-handler',
                description: 'Validation error shown when HTML template source contains an inline event handler attribute',
                defaultMessage: 'Inline event handler attributes are not allowed.'
            });
        case 'unsafe-url-protocol':
            return intl.formatMessage(
                {
                    id: 'content-detail.editor.validation.unsafe-url-protocol',
                    description: 'Validation error shown when HTML template source contains an unsafe URL protocol',
                    defaultMessage: 'Unsafe URL protocol in "{value}".'
                },
                message.values
            );
        case 'data-url':
            return intl.formatMessage({
                id: 'content-detail.editor.validation.data-url',
                description: 'Validation warning shown when HTML template source contains a data URL',
                defaultMessage: 'Data URLs are often blocked by email clients.'
            });
        case 'image-missing-alt':
            return intl.formatMessage({
                id: 'content-detail.editor.validation.image-missing-alt',
                description: 'Validation warning shown when an image in HTML template source has no alt text',
                defaultMessage: 'Image is missing alt text.'
            });
        case 'image-missing-width':
            return intl.formatMessage({
                id: 'content-detail.editor.validation.image-missing-width',
                description: 'Validation warning shown when an image in HTML template source has no explicit width',
                defaultMessage: 'Image has no explicit width, which can make email layout unstable.'
            });
        case 'unknown-placeholder':
            return intl.formatMessage(
                {
                    id: 'content-detail.editor.validation.unknown-placeholder',
                    description: 'Validation warning shown when HTML template source contains an unknown placeholder',
                    defaultMessage: 'Unknown placeholder "{placeholder}".'
                },
                message.values
            );
        case 'unbalanced-placeholder-markers':
            return intl.formatMessage({
                id: 'content-detail.editor.validation.unbalanced-placeholder-markers',
                description: 'Validation warning shown when HTML template placeholder markers are unbalanced',
                defaultMessage: 'Placeholder markers look unbalanced.'
            });
        case 'unsupported-email-css':
            return intl.formatMessage(
                {
                    id: 'content-detail.editor.validation.unsupported-email-css',
                    description: 'Validation warning shown when notification HTML contains CSS that is unreliable in email clients',
                    defaultMessage: '{feature} is not reliably supported by common email clients.'
                },
                message.values
            );
        case 'media-query':
            return intl.formatMessage({
                id: 'content-detail.editor.validation.media-query',
                description: 'Validation warning shown when notification HTML contains a media query',
                defaultMessage: 'Media query support differs between email clients.'
            });
        case 'table-layout-attributes':
            return intl.formatMessage({
                id: 'content-detail.editor.validation.table-layout-attributes',
                description: 'Validation warning shown when a table in notification HTML misses email layout attributes',
                defaultMessage: 'Table layouts should define cellpadding, cellspacing, or role attributes for email clients.'
            });
        case 'large-email':
            return intl.formatMessage({
                id: 'content-detail.editor.validation.large-email',
                description: 'Validation warning shown when notification HTML is large enough to risk inbox clipping',
                defaultMessage: 'Large emails may be clipped by some inboxes.'
            });
    }
};

export const HtmlSourceValidationPanel = (props: HtmlSourceValidationPanelProps) => {
    const intl = useIntl();
    if (props.messages.length === 0) return null;

    return (
        <div className='mx-4 mt-3 flex flex-col gap-2 self-stretch'>
            {props.messages.map((message, index) => {
                const levelLabel =
                    message.level === 'error'
                        ? intl.formatMessage({
                              id: 'content-detail.editor.validation-level.error',
                              description: 'Label for an error severity validation message in the HTML template editor',
                              defaultMessage: 'Error'
                          })
                        : intl.formatMessage({
                              id: 'content-detail.editor.validation-level.warning',
                              description: 'Label for a warning severity validation message in the HTML template editor',
                              defaultMessage: 'Warning'
                          });

                return (
                    <div
                        className={`rounded-md px-3 py-2 text-sm ${message.level === 'error' ? 'bg-status-danger/10 text-status-danger' : 'bg-status-warning/10 text-status-warning'}`}
                        key={`${message.section}-${message.line}-${message.code}-${index}`}>
                        <FormattedMessage
                            id='content-detail.editor.validation-message'
                            description='Validation message row shown below the HTML template source editor'
                            defaultMessage='{level} in {section} line {line}: {message}'
                            values={{
                                level: <span className='font-semibold'>{levelLabel}</span>,
                                section: message.section,
                                line: message.line,
                                message: getValidationMessageText(intl, message)
                            }}
                        />
                    </div>
                );
            })}
        </div>
    );
};
